class TabCustom extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #buttons = [];
  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    if (!this.hasAttribute('data-tab-enhanced')) {
      this.#organizeLayout();
      this.setAttribute('data-tab-enhanced', 'true');
    }

    this.#buttons = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    if (!this.#buttons.length || this.#buttons.length !== this.#panels.length) return;

    this.#buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.#activate(index, true));
      button.addEventListener('keydown', (event) => this.#onKeyDown(event, index));
    });

    const defaultIndex = this.#getDefaultIndex();
    this.#activate(defaultIndex, false);
  }

  #organizeLayout() {
    const list = document.createElement('div');
    list.className = 'tab__list';
    list.setAttribute('role', 'tablist');

    const panels = document.createElement('div');
    panels.className = 'tab__panels';

    const rows = Array.from(this.querySelectorAll('[data-tab-item]'));

    rows.forEach((row) => {
      const listItem = row.querySelector('.tab__list-item');
      const panel = row.querySelector('[data-tab-panel]');

      if (listItem) list.append(listItem);
      if (panel) panels.append(panel);
    });

    this.innerHTML = '';
    this.append(list, panels);
  }

  #getDefaultIndex() {
    const configuredIndex = Number.parseInt(this.dataset.defaultTabIndex || '1', 10);
    if (!Number.isFinite(configuredIndex) || configuredIndex <= 0) return -1;

    const index = configuredIndex - 1;
    return index > this.#buttons.length - 1 ? this.#buttons.length - 1 : index;
  }

  /**
   * @param {number} index
   * @param {boolean} shouldFocus
   */
  #activate(index, shouldFocus) {
    const hasActiveTab = index >= 0;

    this.#buttons.forEach((button, buttonIndex) => {
      const isActive = hasActiveTab && buttonIndex === index;

      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive || (!hasActiveTab && buttonIndex === 0) ? '0' : '-1');

      const panel = this.#panels[buttonIndex];
      if (panel) {
        panel.hidden = !isActive;
      }

      if (isActive && shouldFocus) {
        button.focus();
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   */
  #onKeyDown(event, currentIndex) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();

    let nextIndex = currentIndex;
    const lastIndex = this.#buttons.length - 1;

    if (event.key === 'ArrowRight') nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    if (event.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;

    this.#activate(nextIndex, true);
  }
}

if (!customElements.get('tab-custom')) {
  customElements.define('tab-custom', TabCustom);
}
